/**
 * SHARED CART — NEW STOREFRONT API SYSTEM ONLY.
 *
 * Storefront API is the cross-domain source of truth. The existing theme still
 * renders Shopify's native Ajax cart, so this implementation mirrors:
 *   - successful local Ajax cart mutations -> the Storefront API cart
 *   - an incoming Storefront cart handoff -> the destination Ajax cart
 * Checkout uses the destination store's native Shopify cart.
 */

const initializeStorefrontSharedCart = () => {
  const storefrontSharedCartManager = {
    debug: false,
    config: {},
    storageKey: 'shared-cart:storefront-api:id',
    param: 'storefront_cart_id',
    countParam: 'storefront_cart_count',
    domainPairs: [['heroarts.com', 'spellbinders.com']],
    mutationReg: /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/,
    nativeWriteInProgress: false,
    syncQueue: Promise.resolve(),
    pendingAjaxMutations: Promise.resolve(),
    latestCart: null,
    originalFetch: window.fetch.bind(window),
    cartFields: [
      'id',
      'totalQuantity',
      'lines(first: 250) { nodes {',
      '  id quantity attributes { key value }',
      '  merchandise { ... on ProductVariant { id } }',
      '  sellingPlanAllocation { sellingPlan { id } }',
      '} }' 
    ].join('\n'),

    // Page load - configure and start the new shared cart system.
    init: function () {
      this.config = window.StorefrontSharedCartConfig || {};

      if ( !this.config.apiUrl ) {
        this.log('Storefront API URL is not configured');

        return
      }

      this.debug = this.config.debug ?? this.debug;
      this.param = this.config.handoffParameter || this.param;
      this.domainPairs = this.config.domainPairs || this.domainPairs;

      window.StorefrontSharedCart = {
        getId: () => this.getCartId(),
        getCart: () => this.queryCart(this.getCartId()),
        sync: () => this.enqueueNativeToStorefront('manual'),
        getStatus: () => this.getStatus(),
        reset: () => this.resetCart()
      };

      this.observeNativeCartFetches();
      this.listenForCartEvents();
      this.listenForCrossDomainNavigation();
      this.listenForCheckoutLoading();
      this.boot();
    },
    log: function ( message, ...details ) {
      if ( this.debug ) {
        console.log('[Storefront Shared Cart]', message, ...details);
      }
    },
    startupReloadKey: 'shared-cart:storefront-api:startup-reload',
    startupReloadHappened: function () {
      try {
        return sessionStorage.getItem(this.startupReloadKey) === '1';
      } catch (error) {
        return false;
      }
    },
    markStartupReload: function () {
      try {
        sessionStorage.setItem(this.startupReloadKey, '1');
      } catch (error) {
        this.log('Could not persist startup reload flag', error);
      }
    },
    clearStartupReloadFlag: function () {
      try {
        sessionStorage.removeItem(this.startupReloadKey);
      } catch (error) {
        // ignore
      }
    },
    rootDomain: function ( hostname ) {
      const rootDomain = String(hostname || '').toLowerCase().replace(/^www\./, '');

      return rootDomain;
    },
    sisterDomain: function () {
      const current = this.rootDomain(location.hostname);
      let sister = null;

      for ( let i = 0; i < this.domainPairs.length; i++ ) {
        if ( this.domainPairs[i][0] === current ) {
          sister = this.domainPairs[i][1];
        }
        if ( this.domainPairs[i][1] === current ) {
          sister = this.domainPairs[i][0];
        }
      }

      return sister
    },
    getCartId: function () {
      try {
        const cartId = localStorage.getItem(this.storageKey);

        return cartId
      } catch (error) {
        this.log('Could not fetch Cart ID from storage', error);

        return null
      }
    },
    setCartId: function ( id ) {
      if ( !id ) return

      try {
        localStorage.setItem(this.storageKey, id);
      } catch (error) {
        this.log('Could not set Cart ID in storage', error);
      }
    },
    clearCartId: function () {
      try {
        localStorage.removeItem(this.storageKey);
      } catch (error) {
        this.log('Could not clear Cart ID from storage', error);
      }

      this.latestCart = null;
    },
    // Storefront API request - send a query or mutation to Shopify.
    graphQL: async function ( query, variables = {} ) {
      const response = await this.originalFetch(this.config.apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ query, variables })
      });

      if ( !response.ok ) {
        throw new Error(`Storefront API HTTP ${response.status}`);
      }

      const payload = await response.json();
      if ( payload.errors?.length ) {
        throw new Error(JSON.stringify(payload.errors));
      }
      const data = payload.data;

      return data;
    },
    // After a mutation - validate and store the updated cart.
    mutationCart: function ( payload, operationName ) {
      const errors = payload?.userErrors || [];
      if ( errors.length ) {
        let errorMessage = '';

        for ( let i = 0; i < errors.length; i++ ) {
          const separator = i === 0 ? '' : '; ';
          errorMessage = `${errorMessage}${separator}${errors[i].message}`;
        }

        throw new Error(`${operationName}: ${errorMessage}`);
      }
      if ( !payload?.cart ) {
        throw new Error(`${operationName} returned no cart`);
      }

      this.latestCart = payload.cart;
      this.setCartId(payload.cart.id);

      document.dispatchEvent(new CustomEvent('storefront-shared-cart:updated', {
        detail: { cart: this.latestCart }
      }));

      const cart = this.latestCart;

      return cart;
    },
    // Existing cart - retrieve its current Storefront API state.
    queryCart: async function ( id ) {
      if ( !id ) return null

      const data = await this.graphQL(
        `query SharedCart($id: ID!) { cart(id: $id) { ${this.cartFields} } }`,
        { id }
      );

      if ( !data.cart ) {
        this.clearCartId();

        return null
      }

      this.latestCart = data.cart;
      this.setCartId(data.cart.id);
      const cart = data.cart;

      return cart;
    },
    // First cart action - create the shared Storefront API cart.
    createCart: async function ( lines ) {
      const input = { lines };

      if ( this.config.primaryDomain ) {
        input.attributes = [{ key: 'shared_cart_primary', value: this.config.primaryDomain }];
      }

      const data = await this.graphQL(
        `mutation SharedCartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart { ${this.cartFields} }
            userErrors { field message }
          }
        }`,
        { input }
      );

      const cart = this.mutationCart(data.cartCreate, 'cartCreate');

      return cart;
    },
    // Native cart additions - add missing lines to the shared cart.
    addLines: async function ( cartId, lines ) {
      if ( !lines.length ) {
        const cart = await this.queryCart(cartId);

        return cart;
      }

      const data = await this.graphQL(
        `mutation SharedCartAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart { ${this.cartFields} }
            userErrors { field message }
          }
        }`,
        { cartId, lines }
      );

      const cart = this.mutationCart(data.cartLinesAdd, 'cartLinesAdd');

      return cart;
    },
    // Native quantity changes - update matching shared cart lines.
    updateLines: async function ( cartId, lines ) {
      if ( !lines.length ) {
        const cart = await this.queryCart(cartId);

        return cart;
      }

      const data = await this.graphQL(
        `mutation SharedCartUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart { ${this.cartFields} }
            userErrors { field message }
          }
        }`,
        { cartId, lines }
      );

      const cart = this.mutationCart(data.cartLinesUpdate, 'cartLinesUpdate');

      return cart;
    },
    // Native removals - remove lines that are no longer in the cart.
    removeLines: async function ( cartId, lineIds ) {
      if ( !lineIds.length ) {
        const cart = await this.queryCart(cartId);

        return cart;
      }

      const data = await this.graphQL(
        `mutation SharedCartRemove($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart { ${this.cartFields} }
            userErrors { field message }
          }
        }`,
        { cartId, lineIds }
      );
      
      const cart = this.mutationCart(data.cartLinesRemove, 'cartLinesRemove');

      return cart;
    },
    numericId: function ( gid ) {
      const value = String(gid || '');
      const id = value.slice(value.lastIndexOf('/') + 1);

      return id;
    },
    sortedAttributes: function ( attributes ) {
      const attributesList = attributes || [];
      const sortedAttributes = [];

      for ( let i = 0; i < attributesList.length; i++ ) {
        const attribute = attributesList[i];

        sortedAttributes.push({
          key: String(attribute.key),
          value: String(attribute.value)
        });
      }

      sortedAttributes.sort(( a, b ) => {
        const aValue = `${a.key}\u0000${a.value}`;
        const bValue = `${b.key}\u0000${b.value}`;
        const comparison = aValue.localeCompare(bValue);

        return comparison;
      });

      return sortedAttributes;
    },
    ajaxPropertiesToAttributes: function ( properties ) {
      if ( !properties ) {
        return [];
      }
      
      const propertyAttributes = [];

      for ( const [ key, value ] of Object.entries(properties) ) {
        propertyAttributes.push({
          key: key,
          value: value == null ? '' : String(value)
        });
      }

      const attributes = this.sortedAttributes(propertyAttributes);

      return attributes;
    },
    identity: function ( variantId, sellingPlanId, attributes ) {
      const variantIdentity = this.numericId(variantId);
      const sellingPlanIdentity = this.numericId(sellingPlanId);
      const attributesIdentity = JSON.stringify(this.sortedAttributes(attributes));
      const identity = `${variantIdentity}|${sellingPlanIdentity}|${attributesIdentity}`;

      return identity;
    },
    ajaxLineInput: function ( item ) {
      const line = {
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: item.quantity
      };

      const attributes = this.ajaxPropertiesToAttributes(item.properties);
      if ( attributes.length ) {
        line.attributes = attributes;
      }
      
      if ( item.selling_plan_allocation?.selling_plan ) {
        line.sellingPlanId = `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}`;
      }
      return line;
    },
    // Before synchronization - retrieve the current theme cart.
    fetchNativeCart: async function () {
      const root = window.Shopify?.routes?.root || '/';
      const response = await this.originalFetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if ( !response.ok ) {
        throw new Error(`Ajax cart HTTP ${response.status}`);
      }
      const cart = await response.json();

      return cart;
    },
    // After a native change - calculate and apply Storefront cart differences.
    reconcileStorefrontFromNative: async function ( nativeCart ) {
      const desired = {};
      const nativeCartItems = nativeCart.items || [];

      for ( let i = 0; i < nativeCartItems.length; i++ ) {
        const item = nativeCartItems[i];
        const input = this.ajaxLineInput(item);
        const identity = this.identity(input.merchandiseId, input.sellingPlanId, input.attributes);

        if ( !desired[identity] ) {
          desired[identity] = input;
        } else {
          desired[identity].quantity += input.quantity;
        }
      }

      const desiredLines = Object.values(desired);
      const storedId = this.getCartId();
      if ( !storedId ) {
        const newCart = await this.createCart(desiredLines);

        return newCart;
      }

      const cart = await this.queryCart(storedId);
      if ( !cart ) {
        const newCart = await this.createCart(desiredLines);

        return newCart;
      }

      const existing = {};
      const storefrontCartLines = cart.lines.nodes || [];
      const removals = [];
      const updates = [];
      const additions = [];

      for ( let i = 0; i < storefrontCartLines.length; i++ ) {
        const line = storefrontCartLines[i];
        const planId = line.sellingPlanAllocation?.sellingPlan?.id || null;
        const identity = this.identity(line.merchandise.id, planId, line.attributes);

        if ( !existing[identity] ) {
          existing[identity] = { id: line.id, quantity: line.quantity, merged: false };
        } else {
          // The Storefront cart holds the same variant on more than one line. Keep the
          // first line, fold the rest into it and drop the duplicates so the shared cart
          // matches the (merged) Ajax cart and the startup sync can settle.
          existing[identity].quantity += line.quantity;
          existing[identity].merged = true;
          removals.push(line.id);
          this.log('Merging duplicate Storefront cart line', identity, line.id);
        }
      }

      for ( const [ key, existingLine ] of Object.entries(existing) ) {
        if ( !desired[key] ) {
          removals.push(existingLine.id);
        } else if ( existingLine.merged || desired[key].quantity !== existingLine.quantity ) {
          updates.push({ id: existingLine.id, quantity: desired[key].quantity });
        }
      }

      for ( const [ key, desiredLine ] of Object.entries(desired) ) {
        if ( !existing[key] ) {
          additions.push(desiredLine);
        }
      }

      let updatedCart = cart;

      if ( removals.length ) {
        updatedCart = await this.removeLines(updatedCart.id, removals);
      }

      if ( updates.length ) {
        updatedCart = await this.updateLines(updatedCart.id, updates);
      }

      if ( additions.length ) {
        updatedCart = await this.addLines(updatedCart.id, additions);
      }

      return updatedCart;
    },
    // Cart change detected - queue synchronization to prevent request races.
    enqueueNativeToStorefront: function ( reason ) {
      this.syncQueue = this.syncQueue
        .then(async () => {
          const nativeCart = await this.fetchNativeCart();
          const cart = await this.reconcileStorefrontFromNative(nativeCart);
          this.log('Native -> Storefront sync complete', reason, cart?.totalQuantity);
          return cart;
        })
        .catch(( error ) => console.error('[Storefront Shared Cart] Sync failed:', error));
      const queue = this.syncQueue;

      return queue;
    },
    nativeItemFromLine: function ( line ) {
      const item = { 
        id: this.numericId(line.merchandise.id), 
        quantity: line.quantity 
      };

      if ( line.attributes?.length ) {
        item.properties = {};
        const lineAttributes = line.attributes;

        for ( let i = 0; i < lineAttributes.length; i++ ) {
          const attribute = lineAttributes[i];
          item.properties[attribute.key] = attribute.value;
        }
      }
      
      if ( line.sellingPlanAllocation?.sellingPlan ) {
        item.selling_plan = this.numericId(line.sellingPlanAllocation.sellingPlan.id);
      }
      return item;
    },
    // Build a signature from identity -> total quantity. Quantities are summed per
    // identity so a cart that holds the same variant on several lines (Storefront API)
    // compares equal to one where Shopify merged them into a single line (Ajax cart).
    signatureFromQuantities: function ( quantities ) {
      const lineSignatures = [];

      for ( const [ identity, quantity ] of Object.entries(quantities) ) {
        lineSignatures.push(`${identity}|${quantity}`);
      }

      lineSignatures.sort();
      const signature = lineSignatures.join('\n');

      return signature;
    },
    cartSignatureFromNative: function ( cart ) {
      const cartItems = cart.items || [];
      const quantities = {};

      for ( let i = 0; i < cartItems.length; i++ ) {
        const item = cartItems[i];
        const input = this.ajaxLineInput(item);
        const identity = this.identity(input.merchandiseId, input.sellingPlanId, input.attributes);

        quantities[identity] = (quantities[identity] || 0) + input.quantity;
      }

      return this.signatureFromQuantities(quantities);
    },
    cartSignatureFromStorefront: function ( cart ) {
      const storefrontCartLines = cart.lines.nodes || [];
      const quantities = {};

      for ( let i = 0; i < storefrontCartLines.length; i++ ) {
        const line = storefrontCartLines[i];
        const planId = line.sellingPlanAllocation?.sellingPlan?.id || null;
        const identity = this.identity(line.merchandise.id, planId, line.attributes);

        quantities[identity] = (quantities[identity] || 0) + line.quantity;
      }

      return this.signatureFromQuantities(quantities);
    },
    // After a store change - rebuild the local theme cart from the shared cart.
    replaceNativeFromStorefront: async function ( cart ) {
      const root = window.Shopify?.routes?.root || '/';
      this.nativeWriteInProgress = true;

      try {
        const clearResponse = await this.originalFetch(`${root}cart/clear.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if ( !clearResponse.ok ) {
          throw new Error(`Ajax cart clear HTTP ${clearResponse.status}`);
        }

        const storefrontCartLines = cart.lines.nodes || [];
        const items = [];

        for ( let i = 0; i < storefrontCartLines.length; i++ ) {
          const line = storefrontCartLines[i];
          const nativeItem = this.nativeItemFromLine(line);

          items.push(nativeItem);
        }

        if ( !items.length ) return null

        const addResponse = await this.originalFetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });

        if ( !addResponse.ok ) {
          throw new Error(`Ajax cart rebuild HTTP ${addResponse.status}`);
        }

        const cartData = await addResponse.json();

        return cartData;
      } finally {
        this.nativeWriteInProgress = false;
      }
    },
    // During shopping - watch native add, change, update, and clear requests.
    observeNativeCartFetches: function () {
      window.fetch = ( ...args ) => {
        let requestUrl = '';

        try {
          requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        } catch (error) {
          this.log('Could not inspect fetch URL', error);
        }

        const responsePromise = this.originalFetch(...args);

        if ( !this.nativeWriteInProgress && this.mutationReg.test(requestUrl) ) {
          const observedMutation = responsePromise
            .then(( response ) => {
              if ( response?.ok ) {
                const sync = this.enqueueNativeToStorefront(requestUrl);

                return sync;
              }
            })
            .catch(() => {});
          this.pendingAjaxMutations = Promise
            .all([this.pendingAjaxMutations, observedMutation])
            .then(() => {});
        }

        return responsePromise;
      };
    },
    // During shopping - catch theme or app cart updates announced by pub/sub.
    listenForCartEvents: function () {
      try {
        if ( typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' ) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            if ( !this.nativeWriteInProgress ) {
              this.enqueueNativeToStorefront('theme cart-update event');
            }
          });
        }
      } catch (error) {
        this.log('Could not subscribe to theme cart events', error);
      }
    },
    // Preview testing - add the configured unpublished theme ID when provided.
    addPreviewThemeId: function ( url ) {
      const previewThemeId = this.config.previewThemeId;

      if ( !previewThemeId ) return

      url.searchParams.set('preview_theme_id', previewThemeId);
    },
    // Before a store change - attach the shared cart ID to the destination URL.
    decorateSisterLink: function ( anchor, quantity = null ) {
      const id = this.getCartId();
      const sister = this.sisterDomain();

      if ( !id || !sister || !anchor ) {
        return false
      }

      try {
        const url = new URL(anchor.href, location.href);

        if ( this.rootDomain(url.hostname) !== sister ) {
          return false
        }

        this.addPreviewThemeId(url);
        url.searchParams.set(this.param, id);

        const cartQuantity = quantity ?? this.latestCart?.totalQuantity;
        if ( Number.isInteger(cartQuantity) && cartQuantity >= 0 ) {
          url.searchParams.set(this.countParam, cartQuantity);
        }

        anchor.href = url.toString();

        return true
      } catch (error) {
        this.log('Could not decorate sister-domain link', error);
        return false
      }
    },
    // Store navigation - allow a pending quantity-change debounce to start.
    waitForPendingQuantityChange: function () {
      const quantityInputs = document.querySelectorAll('.js-counter-quantity');
      let hasPendingQuantityChange = false;

      for ( let i = 0; i < quantityInputs.length; i++ ) {
        const quantityInput = quantityInputs[i];
        const renderedQuantity = quantityInput.getAttribute('value');

        if ( quantityInput.value !== renderedQuantity ) {
          hasPendingQuantityChange = true;
        }
      }

      if ( !hasPendingQuantityChange ) return Promise.resolve()

      const debounceTime = typeof ON_CHANGE_DEBOUNCE_TIMER === 'number' ? ON_CHANGE_DEBOUNCE_TIMER : 300;
      const waitTime = debounceTime + 50;
      const pendingQuantityChange = new Promise(( resolve ) => {
        setTimeout(resolve, waitTime);
      });

      return pendingQuantityChange;
    },
    // Store navigation - wait for sync before sending the shopper across domains.
    listenForCrossDomainNavigation: function () {
      document.addEventListener('pointerdown', ( event ) => {
        const anchor = event.target.closest?.('a[href]');
        if ( anchor ) {
          this.decorateSisterLink(anchor);
        }
      }, true);

      document.addEventListener('click', ( event ) => {
        const anchor = event.target.closest?.('a[href]');

        if ( !anchor || event.defaultPrevented || event.button !== 0 || event.metaKey ||
          event.ctrlKey || event.shiftKey || event.altKey ) {
            return
        }

        const sister = this.sisterDomain();
        let url;

        try {
          url = new URL(anchor.href, location.href);
        } catch (error) {
          return
        }
        
        if ( !sister || this.rootDomain(url.hostname) !== sister ) return

        event.preventDefault();
        this.waitForPendingQuantityChange()
          .then(() => this.pendingAjaxMutations)
          .then(() => this.syncQueue)
          .then(() => this.enqueueNativeToStorefront('store change'))
          .then(( cart ) => {
            this.decorateSisterLink(anchor, cart?.totalQuantity);
            location.assign(anchor.href);
        });
      }, true);
    },
    // Checkout click - show the existing spinner while Shopify loads checkout.
    listenForCheckoutLoading: function () {
      document.addEventListener('click', ( event ) => {
        const checkoutButton = event.target.closest?.(
          '.btn-checkout, button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
        );

        if ( !checkoutButton ) return

        checkoutButton.classList.add('is-loading');
      });
    },
    // After a store change - check the URL for an incoming shared cart ID.
    incomingCartId: function () {
      try {
        const cartId = new URL(location.href).searchParams.get(this.param);

        return cartId;
      } catch (error) {
        this.log('Could not read incoming Cart ID', error);
        return null
      }
    },
    incomingCartQuantity: function () {
      try {
        const quantityValue = new URL(location.href).searchParams.get(this.countParam);

        if ( quantityValue === null ) return null

        const quantity = Number(quantityValue);

        if ( !Number.isInteger(quantity) || quantity < 0 ) return null

        return quantity;
      } catch (error) {
        this.log('Could not read incoming cart quantity', error);
        return null
      }
    },
    updateCartCount: function ( quantity ) {
      if ( quantity === null ) return

      const cartCountBubbles = document.querySelectorAll('[data-cart-count-bubble]');

      for ( let i = 0; i < cartCountBubbles.length; i++ ) {
        const cartCountBubble = cartCountBubbles[i];
        cartCountBubble.textContent = quantity;
        cartCountBubble.toggleAttribute('hidden', quantity === 0);
      }
    },
    // After receiving a cart - remove its ID from the visible browser URL.
    cleanHandoffUrl: function () {
      try {
        const url = new URL(location.href);
        url.searchParams.delete(this.param);
        url.searchParams.delete(this.countParam);
        history.replaceState(history.state, '', url.toString());
      } catch (error) {
        this.log('Could not clean cart handoff URL', error);
      } 
    },
    // Debug page - report both carts side by side.
    getStatus: async function () {
      await this.syncQueue;

      const cartId = this.getCartId();
      const [ cart, nativeCart ] = await Promise.all([this.queryCart(cartId), this.fetchNativeCart()]);
      const status = {
        domain: this.rootDomain(location.hostname),
        sisterDomain: this.sisterDomain(),
        primaryDomain: this.config.primaryDomain || null,
        storefrontCartId: cart?.id || cartId || null,
        storefrontCartFound: Boolean(cart),
        storefrontQuantity: cart ? cart.totalQuantity : null,
        storefrontLines: cart ? (cart.lines.nodes || []).length : null,
        nativeCartToken: nativeCart.token || null,
        nativeQuantity: nativeCart.item_count,
        nativeLines: (nativeCart.items || []).length,
        inSync: cart ? this.cartSignatureFromStorefront(cart) === this.cartSignatureFromNative(nativeCart) : null
      };

      return status;
    },
    // Debug page - empty both carts and start the shared cart over.
    resetCart: async function () {
      const root = window.Shopify?.routes?.root || '/';
      const result = { previousCartId: this.getCartId(), removedStorefrontLines: 0 };

      // Let any in-flight theme cart requests and syncs finish first.
      await this.pendingAjaxMutations;
      await this.syncQueue;

      this.nativeWriteInProgress = true;

      try {
        // Storefront API - remove every line so the sister domain empties on its next page load.
        const storedId = this.getCartId();
        const cart = storedId ? await this.queryCart(storedId) : null;
        const storefrontCartLines = cart?.lines?.nodes || [];
        const lineIds = [];

        for ( let i = 0; i < storefrontCartLines.length; i++ ) {
          lineIds.push(storefrontCartLines[i].id);
        }

        if ( lineIds.length ) {
          await this.removeLines(cart.id, lineIds);
          result.removedStorefrontLines = lineIds.length;
        }

        // Native cart - clear items, then blank the note, cart attributes, and discount codes.
        const nativeCart = await this.fetchNativeCart();

        const clearResponse = await this.originalFetch(`${root}cart/clear.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if ( !clearResponse.ok ) {
          throw new Error(`Ajax cart clear HTTP ${clearResponse.status}`);
        }

        const attributes = {};

        for ( const key of Object.keys(nativeCart.attributes || {}) ) {
          attributes[key] = '';
        }

        const updateResponse = await this.originalFetch(`${root}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: '', attributes, discount: '' })
        });

        if ( !updateResponse.ok ) {
          throw new Error(`Ajax cart update HTTP ${updateResponse.status}`);
        }
      } finally {
        this.nativeWriteInProgress = false;
      }

      // Forget the shared cart ID so the next cart action starts a brand new shared cart.
      this.clearCartId();
      this.updateCartCount(0);
      this.log('Shared cart reset', result);

      return result;
    },
    // Startup - receive a cart, migrate a cart, or reconcile stored state.
    boot: function () {
      const incoming = this.incomingCartId();
      if ( incoming ) {
        const incomingQuantity = this.incomingCartQuantity();
        this.updateCartCount(incomingQuantity);
        this.cleanHandoffUrl();
        this.setCartId(incoming);

        this.syncQueue = this.queryCart(incoming)
          .then(( cart ) => {
            if ( !cart ) {
              throw new Error('Incoming Storefront cart was not found');
            }
            const replacement = this.replaceNativeFromStorefront(cart).then(() => {
              this.log('Storefront -> native handoff complete');
              location.reload();
            });

            return replacement;
          })
          .catch(( error ) => console.error('[Storefront Shared Cart] Handoff failed:', error));
        return
      }

      const storedId = this.getCartId();
      if ( !storedId ) {
        this.enqueueNativeToStorefront('initial migration');
        return
      }

      this.syncQueue = Promise
        .all([this.queryCart(storedId), this.fetchNativeCart()])
        .then(( [ cart, nativeCart ] ) => {
          if ( !cart ) {
            const reconciledCart = this.reconcileStorefrontFromNative(nativeCart);

            return reconciledCart;
          }
          if ( this.cartSignatureFromStorefront(cart) === this.cartSignatureFromNative(nativeCart) ) {
            this.clearStartupReloadFlag();
            return cart;
          }

          // Guard against a reload loop: if the previous load already rebuilt the native
          // cart and reloaded, don't do it again this session. Push the native cart up to
          // the Storefront cart instead so the two converge without another reload.
          if ( this.startupReloadHappened() ) {
            console.warn('[Storefront Shared Cart] Carts still differ after a startup rebuild; skipping reload to avoid a loop');
            const reconciledCart = this.reconcileStorefrontFromNative(nativeCart);

            return reconciledCart;
          }

          const replacement = this.replaceNativeFromStorefront(cart).then(() => {
            this.markStartupReload();
            location.reload();
          });

          return replacement;
        })
        .catch(( error ) => console.error('[Storefront Shared Cart] Startup sync failed:', error));
    }
  };

  storefrontSharedCartManager.init();
};

if ( document.readyState === 'loading' ) {
  document.addEventListener('DOMContentLoaded', initializeStorefrontSharedCart);
} else {
  initializeStorefrontSharedCart();
}
